import { useUser } from "@clerk/nextjs";
import { cx } from "class-variance-authority";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { useRouter } from "next/router";
import { type PropsWithChildren, useCallback, useState } from "react";
import { Panel, PanelGroup } from "react-resizable-panels";

import { SidebarCollapse } from "@/assets/icons/navbar/SidebarCollapse";
import HeroBanner from "@/assets/images/branding-cover.png";
import { LoadingState } from "@/components/shared/layout/LoadingState";
import { IconButton } from "@/components/ui/buttons/IconButton/IconButton";
import { WatchlistPage } from "@/components/watchlist/WatchlistPage";
import { RouteConfig } from "@/config/route.config";
import { AuthContext } from "@/data/auth/auth.context";

import { Header } from "./Header";
import { usePanelContext } from "./PanelContext";
import { ResizeHandle } from "./ResizeHandle";

export const Layout = ({ children }: PropsWithChildren) => {
  const {
    panelSizes,
    updatePanelSizes,
    panelGroupRef,
    resetIsAutoResizing,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    setIsLeftPanelMaxed,
    totalPanelWidth,
  } = usePanelContext();
  const handlePanelResize = useCallback(
    (sizes: number[]) => {
      if (sizes.length >= 2) {
        updatePanelSizes(sizes[0], sizes[1]);
        const leftPanelSizeInPx = window.innerWidth * (sizes[0] / 100);
        setIsLeftPanelMaxed(leftPanelSizeInPx >= 820);
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const { isLoaded: isUserLoaded } = useUser();
  const { isInitializing, isLoggedIn } = AuthContext.useAuth();
  const pathname = usePathname();
  const router = useRouter();
  const [showCollapseButton, setShowCollapseButton] = useState(false);
  const isAuthPage = pathname?.includes("sign-in") || pathname?.includes("accept-invite");
  const isBugReporterPage = pathname?.includes("bug-report");

  if (!isUserLoaded) {
    return <LoadingState />;
  }

  // Prevent flash of protected content while auth is initializing
  if (!isAuthPage && !isBugReporterPage && isInitializing) {
    return <LoadingState />;
  }

  // If user is not logged in on a protected page, redirect immediately and show loading
  if (!isAuthPage && !isBugReporterPage && !isInitializing && !isLoggedIn) {
    router.replace(RouteConfig.signin);
    return <LoadingState />;
  }

  if (isAuthPage) {
    return (
      <div className="flex h-screen flex-col bg-interactive-primary-idle">
        <main className="flex-1">{children}</main>
      </div>
    );
  }

  if (isBugReporterPage) {
    return (
      <div className="flex h-screen flex-col bg-elevation-surface-1">
        <main className="flex-1">{children}</main>
      </div>
    );
  }

  const maxSizeDesktop = Math.min(100, (((totalPanelWidth || 800) + 50) / window.innerWidth) * 100);
  const maxSize = maxSizeDesktop;

  const minSizeDesktop = Math.max(30, (430 / window.innerWidth) * 100);
  const minSize = minSizeDesktop;

  return (
    <div className="flex h-screen flex-col">
      <Header />
      <main className="h-full max-h-[calc(100%-60px)] flex-1">
        {isSidebarCollapsed && (
          <div className="flex h-full w-full flex-row">
            <CollapseSidebar setIsSidebarCollapsed={setIsSidebarCollapsed} />
            <div
              data-scroll-container
              className="relative h-full flex-1 overflow-y-auto bg-elevation-surface-1 px-2-5"
            >
              <div className="absolute top-0 right-0 z-0 h-[184px] w-full max-w-[615px]">
                <Image
                  src={HeroBanner}
                  alt="hero-banner"
                  fill
                />
              </div>
              <div className="relative z-10">{children}</div>
            </div>
          </div>
        )}
        {!isSidebarCollapsed && (
          <div className="relative">
            <PanelGroup
              direction="horizontal"
              onLayout={handlePanelResize}
              ref={panelGroupRef}
            >
              <Panel
                defaultSize={panelSizes[0]}
                minSize={minSize}
                maxSize={maxSize}
                id="left-panel"
                onResize={() => setTimeout(() => resetIsAutoResizing(), 100)}
                onMouseEnter={() => setShowCollapseButton(true)}
                onMouseLeave={() => setShowCollapseButton(false)}
              >
                <WatchlistPage />
              </Panel>

              <ResizeHandle />
              <Panel
                defaultSize={panelSizes[1]}
                minSize={30}
                id="right-panel"
              >
                <div
                  data-scroll-container
                  className="relative h-full max-h-[calc(100vh-60px)] flex-1 overflow-y-auto bg-elevation-surface-1 px-2-5"
                >
                  <div className="absolute top-0 right-0 z-0 h-[184px] w-full max-w-[615px]">
                    <Image
                      src={HeroBanner}
                      alt="hero-banner"
                      fill
                    />
                  </div>
                  <div className="relative z-10">{children}</div>
                </div>
              </Panel>
            </PanelGroup>
            {showCollapseButton && (
              <div
                className={cx("absolute top-[54px] z-10 mb-2 flex h-32 w-12 cursor-pointer justify-center pl-5")}
                style={{
                  left: `calc(${panelSizes[0]}% - 4px)`,
                }}
                onMouseEnter={() => setShowCollapseButton(true)}
                onMouseLeave={() => setShowCollapseButton(false)}
              >
                <div className="h-fit w-fit rounded-tr-xs rounded-br-xs border border-elevation-outline-1 bg-elevation-background p-1-5">
                  <IconButton
                    icon={SidebarCollapse}
                    label="Collapse sidebar"
                    variant="transparent"
                    iconClassName="!h-6 !w-6"
                    className="!p-1 ml-auto rotate-180"
                    size="medium"
                    onPress={() => setIsSidebarCollapsed(true)}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

const CollapseSidebar = ({ setIsSidebarCollapsed }: { setIsSidebarCollapsed: (value: boolean) => void }) => {
  return (
    <div className="flex h-full w-auto flex-col items-center justify-start border-elevation-outline-1 border-r bg-elevation-background p-5">
      <IconButton
        icon={SidebarCollapse}
        label="Collapse sidebar"
        variant="transparent"
        size="small"
        iconClassName="!h-4 !w-4"
        className="!p-0"
        onPress={() => setIsSidebarCollapsed(false)}
      />
    </div>
  );
};
